import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Lock, CheckCircle2, Plus, Trash2, Users, HandCoins, Handshake,
  FileText, History, DollarSign, Wallet, PiggyBank, Landmark, TrendingUp, Pencil,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, STAGES, STAGE_LABEL, EXPENSE_CATEGORIES } from "@/lib/finance";
import ExpenseDrawer from "@/components/ExpenseDrawer";

const TABS = [
  { key: "summary", label: "Résumé", icon: TrendingUp },
  { key: "split", label: "Répartition financière", icon: PiggyBank },
  { key: "expenses", label: "Dépenses", icon: Wallet },
  { key: "payments", label: "Paiements", icon: Landmark },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "history", label: "Historique", icon: History },
];

const catLabel = (v) => EXPENSE_CATEGORIES.find((c) => c.value === v)?.label || v;

export default function DealDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [deal, setDeal] = useState(null);
  const [audit, setAudit] = useState([]);
  const [contacts, setContacts] = useState({ partner: [], apporteur: [], commercial: [] });
  const [tab, setTab] = useState("summary");
  const [drawer, setDrawer] = useState({ open: false, expense: null });
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showReason, setShowReason] = useState({ open: false, patch: null });
  const [reason, setReason] = useState("");
  const [savingSplit, setSavingSplit] = useState(false);
  const [addPay, setAddPay] = useState({ open: false, kind: "commission" });
  const [payForm, setPayForm] = useState({ amount: "", currency: "USD", date: new Date().toISOString().slice(0, 10), method: "espece", reference: "", note: "" });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/deals/${id}`);
      setDeal(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [id]);

  const loadContacts = useCallback(async () => {
    const [p, a, c] = await Promise.all([
      api.get("/contacts", { params: { role: "partner" } }),
      api.get("/contacts", { params: { role: "apporteur" } }),
      api.get("/contacts", { params: { role: "commercial" } }),
    ]);
    setContacts({ partner: p.data, apporteur: a.data, commercial: c.data });
  }, []);

  useEffect(() => { load(); loadContacts(); }, [load, loadContacts]);
  useEffect(() => { if (tab === "history") api.get(`/deals/${id}/audit`).then((r) => setAudit(r.data)); }, [tab, id]);

  if (!deal) return <div className="p-10 text-slate-500">Chargement…</div>;

  const f = deal.finance || {};
  const closed = deal.closed;

  const patchDeal = async (patch, requiresReason = false) => {
    if (closed && !requiresReason) {
      setShowReason({ open: true, patch });
      return;
    }
    try {
      const r = await api.put(`/deals/${id}`, patch);
      setDeal(r.data);
      toast.success("Transaction mise à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitReason = async () => {
    if (!reason.trim()) return toast.error("Justification requise");
    try {
      const r = await api.put(`/deals/${id}`, { ...showReason.patch, reason });
      setDeal(r.data);
      toast.success("Correction enregistrée");
      setShowReason({ open: false, patch: null });
      setReason("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doClose = async () => {
    setClosing(true);
    try {
      const r = await api.post(`/deals/${id}/close`);
      setDeal(r.data);
      toast.success("Transaction clôturée");
      setConfirmClose(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setClosing(false);
    }
  };

  const deleteExpense = async (exp) => {
    if (!window.confirm(`Supprimer la dépense « ${exp.label} » ?`)) return;
    try {
      await api.delete(`/deals/${id}/expenses/${exp.id}`);
      toast.success("Dépense supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitPayment = async () => {
    const amount = Number(payForm.amount);
    if (!amount || amount <= 0) return toast.error("Montant invalide");
    try {
      await api.post(`/deals/${id}/payments`, { ...payForm, kind: addPay.kind, amount });
      toast.success("Paiement enregistré");
      setAddPay({ open: false, kind: "commission" });
      setPayForm({ amount: "", currency: "USD", date: new Date().toISOString().slice(0, 10), method: "espece", reference: "", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deletePayment = async (p) => {
    if (!window.confirm("Supprimer ce paiement ?")) return;
    try {
      await api.delete(`/deals/${id}/payments/${p.id}`);
      toast.success("Paiement supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const cur = deal.currency || "USD";

  return (
    <div className="space-y-5" data-testid="deal-detail">
      {/* Context bar */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <button onClick={() => nav("/admin/deals")} className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 mt-1">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold">
              <span className="text-brand-orange">Transaction</span>
              <span className="font-mono text-slate-500">{deal.reference}</span>
              {closed && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold">
                  <Lock className="h-3 w-3" /> Clôturé
                </span>
              )}
            </div>
            <h1 className="mt-1 font-display text-3xl text-brand-navy tracking-tight" data-testid="deal-name">{deal.name}</h1>
            <div className="mt-1.5 flex items-center gap-3 flex-wrap text-sm text-slate-500">
              <span><span className="text-slate-400">Client :</span> <span className="font-medium text-slate-700">{deal.client_name || "—"}</span></span>
              <span><span className="text-slate-400">Offre :</span> {deal.listing_snapshot?.title || "—"}</span>
              <span><span className="text-slate-400">Type :</span> {deal.transaction_type === "sale" ? "Vente" : "Location"}</span>
              <span><span className="text-slate-400">Catégorie :</span> {deal.category || "—"}</span>
              <span><span className="text-slate-400">Responsable :</span> {deal.responsable}</span>
              <span><span className="text-slate-400">Clôture estimée :</span> {deal.estimated_close_date || "—"}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!closed && (
            <Button onClick={() => setConfirmClose(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="deal-close-btn">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Clôturer la transaction
            </Button>
          )}
        </div>
      </div>

      {/* 5 KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        <Kpi label="Prix du bien" value={fmtMoney(f.price, cur)} icon={DollarSign} tone="navy" />
        <Kpi label="Commission brute" value={fmtMoney(f.commission_brute, cur)} icon={HandCoins} tone="orange" />
        <Kpi label="Part Team Work avant dépenses" value={fmtMoney(f.tw_before_expenses, cur)} icon={Handshake} tone="slate" />
        <Kpi label="Total dépenses" value={fmtMoney(f.expenses_total, cur)} icon={Wallet} tone="slate" />
        <Kpi label="Marge nette Team Work" value={fmtMoney(f.margin_net, cur)} icon={TrendingUp} tone={f.margin_net >= 0 ? "emerald" : "red"} strong />
      </div>

      {/* Tabs */}
      <Card>
        <div className="border-b border-slate-200 px-2 flex overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors inline-flex items-center gap-2 ${
                tab === t.key ? "border-brand-orange text-brand-navy" : "border-transparent text-slate-500 hover:text-brand-navy"
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 p-5">
          {/* Main column */}
          <div className="xl:col-span-8 space-y-5">
            {tab === "summary" && (
              <>
                <FormulaBlock f={f} cur={cur} />
                <RepartitionChart f={f} cur={cur} />
              </>
            )}

            {tab === "split" && (
              <SplitTab
                deal={deal}
                cur={cur}
                contacts={contacts}
                onReload={loadContacts}
                onPatch={patchDeal}
                saving={savingSplit}
                setSaving={setSavingSplit}
              />
            )}

            {tab === "expenses" && (
              <ExpensesTab
                deal={deal}
                onAdd={() => setDrawer({ open: true, expense: null })}
                onEdit={(e) => setDrawer({ open: true, expense: e })}
                onDelete={deleteExpense}
                closed={closed}
              />
            )}

            {tab === "payments" && (
              <PaymentsTab
                deal={deal}
                onAdd={(kind) => setAddPay({ open: true, kind })}
                onDelete={deletePayment}
              />
            )}

            {tab === "documents" && (
              <DocumentsTab deal={deal} />
            )}

            {tab === "history" && (
              <AuditTab audit={audit} />
            )}
          </div>

          {/* Side column */}
          <aside className="xl:col-span-4 space-y-4">
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">Résumé financier</div>
              <div className="mt-3 space-y-2">
                <Row label="Prix du bien" value={fmtMoney(f.price, cur)} />
                <Row label="Commission acheteur" value={fmtMoney(f.commission_buyer, cur)} sub={`${deal.rate_buyer}%`} />
                <Row label="Commission vendeur" value={fmtMoney(f.commission_seller, cur)} sub={`${deal.rate_seller}%`} />
                <Row label="Commission brute" value={fmtMoney(f.commission_brute, cur)} bold />
                <Row label="Part partenaire" value={"− " + fmtMoney(f.partner_amount, cur)} tone="red" />
                <Row label="Part TW avant dépenses" value={fmtMoney(f.tw_before_expenses, cur)} bold />
                <Row label="Apporteur" value={"− " + fmtMoney(f.referral_amount, cur)} tone="red" />
                <Row label="Commercial" value={"− " + fmtMoney(f.sales_amount, cur)} tone="red" />
                <Row label="Dépenses" value={"− " + fmtMoney(f.expenses_total, cur)} tone="red" />
                <div className="my-2 border-t border-slate-200"></div>
                <Row
                  label="Marge nette Team Work"
                  value={fmtMoney(f.margin_net, cur)}
                  strong
                  tone={f.margin_net >= 0 ? "emerald" : "red"}
                />
              </div>
            </Card>

            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">Statuts de paiement</div>
              <div className="mt-3 space-y-2">
                <StatusPill label="Commission" v={deal.commission_status} />
                <StatusPill label="Partenaire" v={deal.partner_payment_status} />
                <StatusPill label="Apporteur" v={deal.referral_payment_status} />
                <StatusPill label="Commercial" v={deal.sales_payment_status} />
              </div>
            </Card>

            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">Personnes liées</div>
              <div className="mt-3 space-y-2 text-sm">
                <People icon={Users} label="Partenaire" c={deal.partner} />
                <People icon={Handshake} label="Apporteur" c={deal.apporteur} />
                <People icon={HandCoins} label="Commercial" c={deal.commercial} />
              </div>
            </Card>

            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">Actions rapides</div>
              <div className="mt-3 space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={() => setDrawer({ open: true, expense: null })} disabled={closed} data-testid="quick-add-expense">
                  <Plus className="h-4 w-4 mr-1.5" /> Ajouter une dépense
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => setAddPay({ open: true, kind: "commission" })} data-testid="quick-add-payment">
                  <Wallet className="h-4 w-4 mr-1.5" /> Enregistrer un paiement
                </Button>
                {!closed && (
                  <Button className="w-full justify-start bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setConfirmClose(true)}>
                    <CheckCircle2 className="h-4 w-4 mr-1.5" /> Clôturer la transaction
                  </Button>
                )}
              </div>
            </Card>
          </aside>
        </div>
      </Card>

      {/* Expense drawer */}
      <ExpenseDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, expense: null })}
        dealId={id}
        expense={drawer.expense}
        onSaved={load}
      />

      {/* Close confirmation */}
      <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
        <DialogContent data-testid="confirm-close-dialog">
          <DialogHeader>
            <DialogTitle>Clôturer cette transaction ?</DialogTitle>
            <DialogDescription>
              Une transaction clôturée est verrouillée. Toute modification ultérieure nécessitera une justification.
              La date de clôture réelle sera renseignée automatiquement.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClose(false)}>Annuler</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doClose} disabled={closing} data-testid="confirm-close-btn">
              {closing ? "Clôture…" : "Confirmer la clôture"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reason for closed-deal edits */}
      <Dialog open={showReason.open} onOpenChange={(v) => !v && setShowReason({ open: false, patch: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Correction exceptionnelle</DialogTitle>
            <DialogDescription>
              Ce Deal est clôturé. Merci de justifier la correction (auditée et horodatée).
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex : rectification suite à audit trimestriel" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReason({ open: false, patch: null })}>Annuler</Button>
            <Button onClick={submitReason} className="bg-brand-orange text-white">Enregistrer avec justification</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={addPay.open} onOpenChange={(v) => !v && setAddPay({ open: false, kind: "commission" })}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Enregistrer un paiement</DialogTitle>
            <DialogDescription>Sélectionnez le type de paiement et renseignez les détails.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={addPay.kind} onValueChange={(v) => setAddPay({ ...addPay, kind: v })}>
                <SelectTrigger data-testid="pay-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="commission">Commission (encaissée par Team Work)</SelectItem>
                  <SelectItem value="partner">Paiement partenaire</SelectItem>
                  <SelectItem value="apporteur">Paiement apporteur</SelectItem>
                  <SelectItem value="commercial">Paiement commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label>Montant *</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount" /></div>
              <div>
                <Label>Devise</Label>
                <Select value={payForm.currency} onValueChange={(v) => setPayForm({ ...payForm, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="CDF">CDF</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} /></div>
              <div>
                <Label>Moyen</Label>
                <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="espece">Espèces</SelectItem>
                    <SelectItem value="virement">Virement</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                    <SelectItem value="carte">Carte</SelectItem>
                    <SelectItem value="cheque">Chèque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Référence</Label><Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} placeholder="N° reçu / transaction" /></div>
            <div><Label>Note</Label><Textarea rows={2} value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPay({ open: false, kind: "commission" })}>Annuler</Button>
            <Button onClick={submitPayment} className="bg-brand-orange text-white" data-testid="pay-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Sub-components ----------------

function Kpi({ label, value, icon: Icon, tone = "navy", strong = false }) {
  const tones = {
    navy: "bg-brand-navy text-white",
    orange: "bg-gradient-to-br from-brand-orange to-brand-orangeDark text-white",
    emerald: "bg-gradient-to-br from-emerald-500 to-emerald-700 text-white",
    red: "bg-gradient-to-br from-red-500 to-red-700 text-white",
    slate: "bg-white border border-slate-200 text-brand-navy",
  }[tone];
  return (
    <Card className={`${tones} p-4 ${strong ? "ring-2 ring-brand-orange/60 shadow-lg xl:col-span-1" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-80">{label}</div>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className={`mt-2 font-display ${strong ? "text-3xl" : "text-2xl"} font-extrabold`}>{value}</div>
    </Card>
  );
}

function Row({ label, value, sub, bold, strong, tone }) {
  const t = tone === "red" ? "text-red-600" : tone === "emerald" ? "text-emerald-600" : "text-brand-navy";
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-slate-500">
        {label}{sub && <span className="ml-1 text-[10px] font-mono text-slate-400">({sub})</span>}
      </span>
      <span className={`${bold || strong ? "font-bold" : "font-medium"} ${strong ? "text-lg font-display" : ""} ${t}`}>{value}</span>
    </div>
  );
}

function StatusPill({ label, v }) {
  const map = {
    not_invoiced: { txt: "Non facturée", cls: "bg-slate-100 text-slate-600" },
    invoiced: { txt: "Facturée", cls: "bg-blue-50 text-blue-700" },
    partial: { txt: "Partielle", cls: "bg-amber-50 text-amber-700" },
    paid: { txt: "Payé", cls: "bg-emerald-50 text-emerald-700" },
    to_pay: { txt: "À payer", cls: "bg-amber-50 text-amber-700" },
  };
  const m = map[v] || { txt: v, cls: "bg-slate-100 text-slate-600" };
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${m.cls}`}>{m.txt}</span>
    </div>
  );
}

function People({ icon: Icon, label, c }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-slate-400" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</div>
        <div className="text-sm text-brand-navy font-medium truncate">{c?.name || "—"}</div>
      </div>
    </div>
  );
}

function FormulaBlock({ f, cur }) {
  return (
    <Card className="p-5 bg-gradient-to-br from-slate-50 to-white">
      <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange font-bold">Formule Team Work</div>
      <div className="mt-2 font-mono text-sm text-slate-700 leading-relaxed">
        <div><b className="text-brand-navy">Marge nette transactionnelle Team Work</b> =</div>
        <div className="pl-4">Commission brute totale</div>
        <div className="pl-4 text-red-600">− Part partenaire</div>
        <div className="pl-4 text-red-600">− Commission apporteur</div>
        <div className="pl-4 text-red-600">− Commission commercial</div>
        <div className="pl-4 text-red-600">− Total dépenses transaction</div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-600">Résultat</span>
        <span className={`font-display text-3xl font-extrabold ${f.margin_net >= 0 ? "text-emerald-600" : "text-red-600"}`}>
          {fmtMoney(f.margin_net, cur)}
        </span>
      </div>
    </Card>
  );
}

function RepartitionChart({ f, cur }) {
  const items = [
    { label: "Commission brute", v: f.commission_brute, cls: "bg-brand-orange" },
    { label: "Partenaire", v: f.partner_amount, cls: "bg-blue-500" },
    { label: "Apporteur", v: f.referral_amount, cls: "bg-indigo-500" },
    { label: "Commercial", v: f.sales_amount, cls: "bg-violet-500" },
    { label: "Dépenses", v: f.expenses_total, cls: "bg-slate-400" },
    { label: "Marge Team Work", v: Math.max(0, f.margin_net || 0), cls: "bg-emerald-500" },
  ];
  const max = Math.max(...items.map((i) => i.v || 0), 1);
  return (
    <Card className="p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">Répartition</div>
      <div className="mt-4 space-y-2.5">
        {items.map((it) => (
          <div key={it.label}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-600">{it.label}</span>
              <span className="font-mono font-semibold text-brand-navy">{fmtMoney(it.v, cur)}</span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full ${it.cls} rounded-full`} style={{ width: `${((it.v || 0) / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SplitTab({ deal, cur, contacts, onReload, onPatch, saving, setSaving }) {
  const [rates, setRates] = useState({ rate_buyer: deal.rate_buyer, rate_seller: deal.rate_seller });
  const [partner, setPartner] = useState({
    partner_id: deal.partner_id || "",
    partner_split_type: deal.partner_split_type,
    partner_split_value: deal.partner_split_value,
  });
  const [apporteur, setApporteur] = useState({
    apporteur_id: deal.apporteur_id || "",
    referral_base: deal.referral_base,
    referral_type: deal.referral_type,
    referral_value: deal.referral_value,
  });
  const [commercial, setCommercial] = useState({
    commercial_id: deal.commercial_id || "",
    sales_base: deal.sales_base,
    sales_type: deal.sales_type,
    sales_value: deal.sales_value,
  });
  const [addContact, setAddContact] = useState({ open: false, role: "partner", name: "", phone: "", email: "" });

  const saveAll = async () => {
    setSaving(true);
    try {
      const patch = {
        rate_buyer: Number(rates.rate_buyer),
        rate_seller: Number(rates.rate_seller),
        partner_id: partner.partner_id || null,
        partner_split_type: partner.partner_split_type,
        partner_split_value: Number(partner.partner_split_value || 0),
        apporteur_id: apporteur.apporteur_id || null,
        referral_base: apporteur.referral_base,
        referral_type: apporteur.referral_type,
        referral_value: Number(apporteur.referral_value || 0),
        commercial_id: commercial.commercial_id || null,
        sales_base: commercial.sales_base,
        sales_type: commercial.sales_type,
        sales_value: Number(commercial.sales_value || 0),
      };
      await onPatch(patch);
    } finally {
      setSaving(false);
    }
  };

  const createContact = async () => {
    if (!addContact.name.trim()) return toast.error("Nom requis");
    try {
      const r = await api.post("/contacts", addContact);
      toast.success("Contact créé");
      await onReload();
      if (addContact.role === "partner") setPartner((p) => ({ ...p, partner_id: r.data.id }));
      if (addContact.role === "apporteur") setApporteur((a) => ({ ...a, apporteur_id: r.data.id }));
      if (addContact.role === "commercial") setCommercial((c) => ({ ...c, commercial_id: r.data.id }));
      setAddContact({ open: false, role: "partner", name: "", phone: "", email: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const f = deal.finance;
  const commissionBuyer = (deal.price * (Number(rates.rate_buyer) || 0)) / 100;
  const commissionSeller = (deal.price * (Number(rates.rate_seller) || 0)) / 100;
  const commissionBrute = commissionBuyer + commissionSeller;

  return (
    <div className="space-y-6" data-testid="tab-split-content">
      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">Répartition financière</div>
            <h3 className="mt-1 font-display text-xl text-brand-navy">Taux & commissions</h3>
          </div>
          <Button onClick={saveAll} disabled={saving} className="bg-brand-orange text-white" data-testid="split-save">
            {saving ? "Enregistrement…" : "Enregistrer les changements"}
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Élément</TableHead>
              <TableHead>Base</TableHead>
              <TableHead>Taux</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              <TableHead>Statut</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-semibold">Prix du bien</TableCell>
              <TableCell>—</TableCell>
              <TableCell>—</TableCell>
              <TableCell className="text-right font-mono">{fmtMoney(deal.price, cur)}</TableCell>
              <TableCell>—</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Commission acheteur</TableCell>
              <TableCell>Prix</TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Input type="number" step="0.01" className="h-8 w-20" value={rates.rate_buyer} onChange={(e) => setRates({ ...rates, rate_buyer: e.target.value })} data-testid="rate-buyer" />
                  <span className="text-xs text-slate-500">%</span>
                </div>
              </TableCell>
              <TableCell className="text-right font-mono">{fmtMoney(commissionBuyer, cur)}</TableCell>
              <TableCell><StatusPill label="" v={deal.commission_status} /></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Commission vendeur</TableCell>
              <TableCell>Prix</TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Input type="number" step="0.01" className="h-8 w-20" value={rates.rate_seller} onChange={(e) => setRates({ ...rates, rate_seller: e.target.value })} data-testid="rate-seller" />
                  <span className="text-xs text-slate-500">%</span>
                </div>
              </TableCell>
              <TableCell className="text-right font-mono">{fmtMoney(commissionSeller, cur)}</TableCell>
              <TableCell><StatusPill label="" v={deal.commission_status} /></TableCell>
            </TableRow>
            <TableRow className="bg-brand-orange/5">
              <TableCell className="font-bold text-brand-navy">Commission brute totale</TableCell>
              <TableCell>Somme</TableCell>
              <TableCell>—</TableCell>
              <TableCell className="text-right font-mono font-bold text-brand-orange">{fmtMoney(commissionBrute, cur)}</TableCell>
              <TableCell>—</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>

      <SplitPanel
        title="Partage partenaire"
        icon={Users}
        contact={contacts.partner}
        value={{ id: partner.partner_id, type: partner.partner_split_type, value: partner.partner_split_value }}
        onChange={(v) => setPartner({ partner_id: v.id || "", partner_split_type: v.type, partner_split_value: v.value })}
        onNewContact={() => setAddContact({ open: true, role: "partner", name: "", phone: "", email: "" })}
        baseAmount={commissionBrute}
        cur={cur}
        computed={f.partner_amount}
        note="Formule : Part TW avant dépenses = Commission brute − Part partenaire"
        status={<StatusPill label="Statut" v={deal.partner_payment_status} />}
        showBase={false}
      />

      <SplitPanel
        title="Commission apporteur"
        icon={Handshake}
        contact={contacts.apporteur}
        value={{ id: apporteur.apporteur_id, base: apporteur.referral_base, type: apporteur.referral_type, value: apporteur.referral_value }}
        onChange={(v) => setApporteur({ apporteur_id: v.id || "", referral_base: v.base, referral_type: v.type, referral_value: v.value })}
        onNewContact={() => setAddContact({ open: true, role: "apporteur", name: "", phone: "", email: "" })}
        cur={cur}
        computed={f.referral_amount}
        note="Base : commission brute, part TW ou montant fixe."
        status={<StatusPill label="Statut" v={deal.referral_payment_status} />}
        showBase
      />

      <SplitPanel
        title="Commission commercial"
        icon={HandCoins}
        contact={contacts.commercial}
        value={{ id: commercial.commercial_id, base: commercial.sales_base, type: commercial.sales_type, value: commercial.sales_value }}
        onChange={(v) => setCommercial({ commercial_id: v.id || "", sales_base: v.base, sales_type: v.type, sales_value: v.value })}
        onNewContact={() => setAddContact({ open: true, role: "commercial", name: "", phone: "", email: "" })}
        cur={cur}
        computed={f.sales_amount}
        note="Rémunération du commercial ayant conclu le Deal."
        status={<StatusPill label="Statut" v={deal.sales_payment_status} />}
        showBase
      />

      {/* Contact dialog */}
      <Dialog open={addContact.open} onOpenChange={(v) => !v && setAddContact({ ...addContact, open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau {addContact.role === "partner" ? "partenaire" : addContact.role === "apporteur" ? "apporteur" : "commercial"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom *</Label><Input value={addContact.name} onChange={(e) => setAddContact({ ...addContact, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Téléphone</Label><Input value={addContact.phone} onChange={(e) => setAddContact({ ...addContact, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={addContact.email} onChange={(e) => setAddContact({ ...addContact, email: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddContact({ ...addContact, open: false })}>Annuler</Button>
            <Button className="bg-brand-orange text-white" onClick={createContact}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SplitPanel({ title, icon: Icon, contact, value, onChange, onNewContact, cur, computed, note, status, showBase }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-brand-orange" />
          <h3 className="font-display text-lg text-brand-navy">{title}</h3>
        </div>
        {status}
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <Label>{title.includes("partenaire") ? "Partenaire" : title.includes("apporteur") ? "Apporteur" : "Commercial"}</Label>
          <div className="flex gap-2">
            <Select value={value.id || "none"} onValueChange={(v) => onChange({ ...value, id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid={`sel-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {contact.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={onNewContact}><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        {showBase && (
          <div>
            <Label>Base</Label>
            <Select value={value.base} onValueChange={(v) => onChange({ ...value, base: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="brute">Commission brute</SelectItem>
                <SelectItem value="net_tw">Part Team Work</SelectItem>
                <SelectItem value="fixed">Montant fixe</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Type</Label>
          <Select value={value.type} onValueChange={(v) => onChange({ ...value, type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun</SelectItem>
              <SelectItem value="pct">Pourcentage (%)</SelectItem>
              <SelectItem value="amount">Montant fixe</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Valeur</Label>
          <Input type="number" step="0.01" value={value.value} onChange={(e) => onChange({ ...value, value: e.target.value })} />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="text-slate-500 italic">{note}</span>
        <span className="font-mono font-bold text-brand-navy text-lg">{fmtMoney(computed, cur)}</span>
      </div>
    </Card>
  );
}

function ExpensesTab({ deal, onAdd, onEdit, onDelete, closed }) {
  const expenses = deal.expenses || [];
  const total = deal.finance?.expenses_total || 0;
  const cur = deal.currency;
  const last = expenses[0];
  return (
    <div className="space-y-4" data-testid="tab-expenses-content">
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4"><div className="text-xs text-slate-500">Total dépenses</div><div className="mt-1 font-display text-2xl font-bold text-brand-navy">{fmtMoney(total, cur)}</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500">Nombre</div><div className="mt-1 font-display text-2xl font-bold text-brand-navy">{expenses.length}</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500">Dernière</div><div className="mt-1 font-medium text-brand-navy text-sm">{last ? `${last.label} · ${fmtMoney(last.amount, last.currency)}` : "—"}</div></Card>
      </div>
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg text-brand-navy">Dépenses du Deal</h3>
        <Button onClick={onAdd} disabled={closed} className="bg-brand-orange hover:bg-brand-orangeDark text-white" data-testid="add-expense-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Ajouter une dépense
        </Button>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Libellé</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              <TableHead>Moyen</TableHead>
              <TableHead>Justificatif</TableHead>
              <TableHead>Enregistré par</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Aucune dépense enregistrée pour ce Deal.</TableCell></TableRow>
            )}
            {expenses.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                <TableCell className="font-mono text-xs">{e.date}</TableCell>
                <TableCell className="font-medium">{e.label}</TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{catLabel(e.category)}</span></TableCell>
                <TableCell className="text-right font-mono">{fmtMoney(e.amount, e.currency)}</TableCell>
                <TableCell className="text-xs">{e.payment_method}</TableCell>
                <TableCell>
                  {e.receipt_url ? <a href={e.receipt_url} target="_blank" rel="noreferrer" className="text-brand-orange hover:underline text-xs">Voir</a> : <span className="text-xs text-slate-400">—</span>}
                </TableCell>
                <TableCell className="text-xs text-slate-500">{e.created_by}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => onEdit(e)} disabled={closed}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(e)} disabled={closed}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function PaymentsTab({ deal, onAdd, onDelete }) {
  const payments = deal.payments || [];
  const groups = ["commission", "partner", "apporteur", "commercial"];
  const groupLabel = { commission: "Commissions encaissées", partner: "Paiements partenaire", apporteur: "Paiements apporteur", commercial: "Paiements commercial" };
  return (
    <div className="space-y-4" data-testid="tab-payments-content">
      <div className="flex flex-wrap items-center gap-2">
        {groups.map((g) => (
          <Button key={g} variant="outline" onClick={() => onAdd(g)} data-testid={`add-pay-${g}`}>
            <Plus className="h-4 w-4 mr-1" /> {groupLabel[g]}
          </Button>
        ))}
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              <TableHead>Moyen</TableHead>
              <TableHead>Référence</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">Aucun paiement enregistré.</TableCell></TableRow>
            )}
            {payments.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-mono text-xs">{p.date}</TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{groupLabel[p.kind]}</span></TableCell>
                <TableCell className="text-right font-mono">{fmtMoney(p.amount, p.currency)}</TableCell>
                <TableCell className="text-xs">{p.method}</TableCell>
                <TableCell className="font-mono text-xs">{p.reference || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500 truncate max-w-xs">{p.note || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => onDelete(p)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function DocumentsTab({ deal }) {
  const items = [
    { key: "invoice_buyer", label: "Facture acheteur", desc: "Facture émise à l'acheteur pour la commission" },
    { key: "invoice_seller", label: "Facture vendeur", desc: "Facture émise au vendeur pour la commission" },
    { key: "receipt", label: "Reçu de commission", desc: "Reçu délivré au client après paiement" },
    { key: "note_partner", label: "Note de commission partenaire", desc: "Justificatif de part partagée" },
    { key: "note_apporteur", label: "Bon de paiement apporteur", desc: "Justificatif de rémunération apporteur" },
    { key: "note_commercial", label: "Bon de paiement commercial", desc: "Justificatif de rémunération commercial" },
  ];
  return (
    <div className="space-y-4" data-testid="tab-documents-content">
      <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3 text-sm text-amber-800">
        Les documents seront générés à partir du Deal et rattachés à la <Link to="/admin/invoices" className="underline font-semibold">facturation Team Work</Link>. Le module PDF est branché à l&apos;endpoint <span className="font-mono">/api/invoices</span>.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((it) => (
          <Card key={it.key} className="p-4">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-brand-orange mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-brand-navy">{it.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{it.desc}</div>
                <div className="mt-3 flex gap-2">
                  <Button variant="outline" disabled className="opacity-60">Générer</Button>
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 self-center">Bientôt</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function AuditTab({ audit }) {
  if (!audit.length) return <div className="p-6 text-sm text-slate-500 text-center">Aucun événement enregistré pour ce Deal.</div>;
  return (
    <div className="space-y-2" data-testid="tab-history-content">
      {audit.map((e) => (
        <Card key={e.id} className="p-3">
          <div className="flex items-center justify-between text-xs">
            <span className="font-mono text-slate-400">{e.at?.slice(0, 19).replace("T", " ")}</span>
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-bold">{e.action}</span>
          </div>
          <div className="mt-1 text-sm text-brand-navy">
            <span className="font-semibold">{e.user_name || "Système"}</span>
            {e.field && <span className="text-slate-500"> — champ <span className="font-mono">{e.field}</span></span>}
          </div>
          {(e.old_value !== null || e.new_value !== null) && (
            <div className="mt-1 text-xs font-mono text-slate-500">
              {e.old_value !== null && <span className="text-red-500">−{JSON.stringify(e.old_value)}</span>}
              {e.old_value !== null && e.new_value !== null && <span className="mx-2">→</span>}
              {e.new_value !== null && <span className="text-emerald-600">+{JSON.stringify(e.new_value)}</span>}
            </div>
          )}
          {e.reason && <div className="mt-1 text-xs text-amber-700 italic">Justification : {e.reason}</div>}
        </Card>
      ))}
    </div>
  );
}
